package com.bikompanion.ui.composables

import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Directions
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import dev.chrisbanes.haze.HazeState
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.ParkingType
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewLightDarkExpanded
import com.bikompanion.ui.strings.LocalStrings

@Composable
internal fun ParkingDetailsSheet(
    parkingSpot: BikeParkingSpot?,
    hazeState: HazeState,
    onDismiss: () -> Unit,
    docked: Boolean = false,
    dockedWidth: Dp = DockedSheetWidth,
) {
    // The spot goes null the instant the sheet is dismissed, so the last-known values are kept
    // around long enough for GlassBottomSheet's exit transition to still have something to draw.
    var lastSpot by remember { mutableStateOf(parkingSpot) }
    if (parkingSpot != null) {
        lastSpot = parkingSpot
    }
    // Keyed on the spot so that tapping another marker doesn't carry an open "Details" over.
    var detailsExpanded by remember(lastSpot?.id) { mutableStateOf(false) }
    GlassBottomSheet(
        visible = parkingSpot != null,
        hazeState = hazeState,
        onDismiss = onDismiss,
        docked = docked,
        dockedWidth = dockedWidth,
    ) {
        val currentSpot = lastSpot ?: return@GlassBottomSheet
        val openDirections = rememberDirectionsLauncher(
            latitude = currentSpot.latitude,
            longitude = currentSpot.longitude,
            label = currentSpot.type.toDisplayName(),
        )
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = currentSpot.type.toDisplayName(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.weight(1f),
            )
            IconButton(
                onClick = openDirections,
            ) {
                Icon(
                    imageVector = Icons.Default.Directions,
                    contentDescription = LocalStrings.current.actionGetDirections,
                )
            }
        }
        currentSpot.capacity?.let {
            ParkingAvailabilityBar(
                capacity = it,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 12.dp),
            )
        }

        currentSpot.address?.let {
            StationField(LocalStrings.current.fieldAddress, it)
        }
        currentSpot.isCovered?.let {
            StationField(LocalStrings.current.fieldCovered, if (it) LocalStrings.current.fieldYes else LocalStrings.current.fieldNo)
        }
        ExpandableDetails(
            expanded = detailsExpanded,
            onToggle = { detailsExpanded = !detailsExpanded },
        ) {
            StationField(LocalStrings.current.fieldId, currentSpot.id)
            StationField(LocalStrings.current.fieldCoordinates, "${currentSpot.latitude}, ${currentSpot.longitude}")
        }
    }
}

@Composable
private fun ParkingType.toDisplayName() =
    LocalStrings.current.let { strings ->
        when (this) {
            ParkingType.Stands -> strings.parkingTypeStands
            ParkingType.Shed -> strings.parkingTypeShed
            ParkingType.Lockers -> strings.parkingTypeLockers
            ParkingType.Building -> strings.parkingTypeBuilding
            ParkingType.Other -> strings.parkingTypeOther
        }
    }

/** Every field filled in; the previews below take them away one type at a time. */
private val PreviewParkingSpot = BikeParkingSpot(
    id = "node/1234",
    latitude = 41.387,
    longitude = 2.17,
    capacity = 12,
    isCovered = true,
    address = "Carrer de Balmes, 1",
    type = ParkingType.Stands,
)

@PreviewLightDarkCompact
@Composable
private fun ParkingDetailsSheetAllFieldsPreview() {
    ParkingDetailsSheetPreview(spot = PreviewParkingSpot)
}

@PreviewLightDarkCompact
@Composable
private fun ParkingDetailsSheetUncoveredPreview() {
    ParkingDetailsSheetPreview(spot = PreviewParkingSpot.copy(type = ParkingType.Shed, isCovered = false, address = null))
}

@PreviewLightDarkCompact
@Composable
private fun ParkingDetailsSheetNoCapacityPreview() {
    ParkingDetailsSheetPreview(spot = PreviewParkingSpot.copy(type = ParkingType.Lockers, capacity = null))
}

@PreviewLightDarkCompact
@Composable
private fun ParkingDetailsSheetCapacityOnlyPreview() {
    ParkingDetailsSheetPreview(spot = PreviewParkingSpot.copy(type = ParkingType.Building, isCovered = null, address = null))
}

@PreviewLightDarkCompact
@Composable
private fun ParkingDetailsSheetNothingTaggedPreview() {
    ParkingDetailsSheetPreview(
        spot = PreviewParkingSpot.copy(type = ParkingType.Other, capacity = null, isCovered = null, address = null),
    )
}

@PreviewLightDarkExpanded
@Composable
private fun ParkingDetailsSheetDockedPreview() {
    ParkingDetailsSheetPreview(spot = PreviewParkingSpot, docked = true)
}

@Composable
private fun ParkingDetailsSheetPreview(
    spot: BikeParkingSpot,
    docked: Boolean = false,
) {
    GlassPreviewSurface { hazeState ->
        ParkingDetailsSheet(parkingSpot = spot, hazeState = hazeState, onDismiss = {}, docked = docked)
    }
}
